import PdfViewer from './components/PdfViewer';
import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { RootState } from '../store/store';
import { Button, Form, Modal } from 'react-bootstrap';
import { clearTenantVpaMapping, fetchVpaTenantMapping, verifyUTR } from '../features/tenant_vpa_mapping/TenantVpaSlice';
import logo from './assets/logo.svg';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import './styles/GeneralLayout.scss';
import './styles/GeneralElement.scss';
import './styles/OveriddenVariables.scss';
import { jwtDecode } from 'jwt-decode';
import { MdError } from 'react-icons/md';
import { IoCheckmarkCircleSharp } from "react-icons/io5";


interface TokenPayload {
  tenantID: number;
  vpaId: number;
  iat: number;
  exp: number;
}
interface VerifyTokenResult {
  valid: boolean;
  data?: TokenPayload;
  error?: string;
}

const verifyToken = (token: string): VerifyTokenResult => {
  try {
    if (!token) {
      throw new Error('Token is not provided.');
    }

    const decoded = jwtDecode<TokenPayload>(token);

    // Get the current time in seconds
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if the token has expired
    if (decoded.exp < currentTime) {
      throw new Error('Link has expired');
    }
    return { valid: true, data: decoded };
  } catch (error) {
    return { valid: false, error: (error as Error).message };
  }
};

interface FormData {
  utr: string;
  name: string;
  agree: boolean;
}

interface FormErrors {
  utr?: string;
  agree?: string;
  name?: string;
}

function App() {
  const dispatch = useAppDispatch();

  const defaultConsentText = 'I agree to the terms and conditions';
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [linkError, setLinkError] = useState<null | string>(null);

  const { loading, error, mapping, vpa_consent } = useAppSelector((state: RootState) => state.tenant_vpa_mapping);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    utr: '',
    name: '',
    agree: false,
  });
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const checkToken = () => {
    const data = verifyToken(currentUrl);
    console.log(data);
    if (data.valid && data.data) {
      dispatch(fetchVpaTenantMapping({ tenantId: data.data.tenantID, vpaId: data.data.vpaId }));
    } else if (data.error) {
      setLinkError(data.error);
    } else {
      setLinkError('Link Not Valid');
    }
  };

  useEffect(() => {
    setCurrentUrl(window.location.pathname.slice(1));
  }, [window.location.pathname]);

  useEffect(() => {
    checkToken();
  }, [currentUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });

    setFormErrors({
      ...formErrors,
      [id]: '',
    });
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    if (!formData.utr) {
      errors.utr = 'Please enter UTR number';
    }
    if (!formData.name) {
      errors.name = 'Please enter your name';
    }

    if (!formData.agree) {
      errors.agree = 'Please agree to terms & conditions';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (mapping) {
      const response = await dispatch(
        verifyUTR({
          vpaId: mapping[0].vpa[0].vpaId,
          tenantId: mapping[0].tenantId,
          utr: formData.utr,
          name: formData.name,
          termCondition: window.env.CONSENT_PDF,
          consentText: window.env.CONSENT_TEXT
        })
      );

      setTimeout(() => {
        dispatch(clearTenantVpaMapping());
      }, 3000);
    }

    setFormErrors({});
  };

  const getContent = () => {

   
    if (mapping) {
      if(mapping[0].vpa[0].has_consent){
        return (
          <div className="utr-page">
            <div className="success_content">
              <IoCheckmarkCircleSharp />
              VPA already verfied
            </div>
          </div>
        );
      }
      else return (
        <div className="utr-page">
          {loading ? (
            <div className="loading-backdrop">
              <div className="spinner"></div>
            </div>
          ) : (
            <></>
          )}
          {vpa_consent ? <div className="success-popup">UTR Verified</div> : <></>}

          {error ? <div className="error-popup">{error}</div> : <></>}

          <img src={logo} alt="Fincentive Logo" className="logo" />

          <div className="utr-container">
            <div className="header">
              <img src={mapping[0].storeLogo} />
              <div className="title">{mapping[0].tenantName}</div>
            </div>

            <div className="banner">
              <img src={mapping[0].storeImage} />
            </div>

            <form className="padded-section" onSubmit={handleSubmit}>
              <Form.Group controlId="vpa" className="input-group">
                <Form.Label className="border-label">VPA</Form.Label>
                <Form.Control type="text" readOnly value={mapping[0].vpa[0].vpa} />
              </Form.Group>
              <Form.Group controlId="transactionDate" className="input-group">
                <Form.Label className="border-label">Transaction Date</Form.Label>
                <Form.Control type="date" readOnly value={mapping[0].vpa[0].utr.transactionDate.split("T")[0]} />
              </Form.Group>
              <Form.Group controlId="utr" className="input-group">
                <Form.Label className="border-label">UTR Number</Form.Label>
                <Form.Control type="text" value={formData.utr} onChange={handleChange} placeholder="UTR" isInvalid={!!formErrors.utr} />
                {formErrors.utr && <Form.Control.Feedback type="invalid">{formErrors.utr}</Form.Control.Feedback>}
              </Form.Group>
              <Form.Group controlId="name" className="input-group">
                <Form.Label className="border-label">Name</Form.Label>
                <Form.Control type="text" value={formData.name} onChange={handleChange} placeholder="Name" isInvalid={!!formErrors.name} />
                {formErrors.name && <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>}
              </Form.Group>

              <Form.Group controlId="termsCheckbox">
                <div className="d-flex ">
                  <Form.Check
                    type="checkbox"
                    id="agreeCheckbox"
                    onChange={(e) => {
                      if (e.target.value === 'true') {
                        setFormData({
                          ...formData,
                          agree: false,
                        });
                      }
                      if (e.target.value === 'false') {
                        setFormData({
                          ...formData,
                          agree: true,
                        });
                      }
                      setFormErrors((prevState) => {
                        delete prevState['agree'];
                        return prevState;
                      });
                    }}
                    className="hollow-checkbox me-2"
                    value={formData.agree ? 'true' : 'false'}
                  />
                  <label
                    onClick={() => {
                      handleShow();
                    }}
                    htmlFor="agreeCheckbox"
                    className="terms_n_condition"
                  >
                    {window.env.CONSENT_TEXT}
                  </label>
                </div>
                {formErrors.agree && <Form.Control.Feedback type="invalid">{formErrors.agree}</Form.Control.Feedback>}
              </Form.Group>

              <Button type="submit" className="utr-button">
                Verify
              </Button>
            </form>
          </div>

          <PdfViewer terms_n_condition={window.env.CONSENT_PDF} show={show} handleClose={handleClose} />
        </div>
      );
    }
    
    if (loading) {
      return (
        <div className="utr-page">
          <div className="loading">
            <div className="spinner"></div> {/* Add CSS for the spinner */}
          </div>
        </div>
      );
    }
    if (linkError) {
      return (
        <div className="utr-page">
          <div className="error_content">
            <MdError />
            {linkError}
          </div>
        </div>
      );
    }
  };

  return <>{getContent()}</>;
}

export default App;
